import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './BookingDetail.css';

function BookingDetail({ order, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const { t } = useLanguage();
  if (!order) return null;

  const lines = order.lines && order.lines.length > 0 ? order.lines : null;
  const statusLabel = order.approval_status === 'APPROVED' ? t('bookingApproved')
    : order.approval_status === 'REJECTED' ? t('bookingRejected')
    : order.approval_status === 'PENDING_APPROVAL' ? t('bookingPending')
    : t('bookingAccepted');

  const statusClass = order.approval_status === 'REJECTED' ? 'rejected'
    : order.approval_status === 'PENDING_APPROVAL' ? 'pending' : 'approved';

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  return (
    <div className="bd-overlay" onClick={onClose}>
      <div className="bd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bd-header">
          <div>
            <h2 className="bd-ref">{order.reference || order.booking_reference || `#${order.booking_id}`}</h2>
            <span className={`bd-status bd-status--${statusClass}`}>{statusLabel}</span>
            {order.locked && <span className="bd-locked">Låst</span>}
          </div>
          <button className="bd-close" onClick={onClose}>&#10005;</button>
        </div>

        {order.rejection_reason && (
          <div className="bd-rejection">
            <strong>Afvisningsgrund:</strong> {order.rejection_reason}
          </div>
        )}

        <div className="bd-section">
          <div className="bd-row">
            <div className="bd-col">
              <label>Afsender</label>
              <strong>{order.sender_name || order.pickup_name}</strong>
              {order.pickup_address && <span>{order.pickup_address}</span>}
              {order.pickup_city && <span>{order.pickup_postal_code} {order.pickup_city}</span>}
            </div>
            <div className="bd-col">
              <label>Modtager</label>
              <strong>{order.recipient_name || order.delivery_name}</strong>
              {order.delivery_address && <span>{order.delivery_address}</span>}
              {order.delivery_city && <span>{order.delivery_postal_code} {order.delivery_city}</span>}
            </div>
          </div>
        </div>

        <div className="bd-section">
          <div className="bd-row">
            <div className="bd-col">
              <label>Afhentning</label>
              <strong>{formatDate(order.pickup_date)}</strong>
              {order.pickup_time && <span>kl. {order.pickup_time.substring(0, 5)}</span>}
            </div>
            <div className="bd-col">
              <label>Levering</label>
              <strong>{order.delivery_date ? formatDate(order.delivery_date) : '-'}</strong>
              {order.delivery_time && <span>kl. {order.delivery_time.substring(0, 5)}</span>}
            </div>
          </div>
          {(order.pickup_notes || order.delivery_notes) && (
            <div className="bd-row">
              {order.pickup_notes && <div className="bd-col"><label>Afhentning note</label><span>{order.pickup_notes}</span></div>}
              {order.delivery_notes && <div className="bd-col"><label>Levering note</label><span>{order.delivery_notes}</span></div>}
            </div>
          )}
        </div>

        {order.cmr_number && (
          <div className="bd-section bd-meta">
            <span>CMR: <strong>{order.cmr_number}</strong></span>
            {order.po_number && <span>PO: <strong>{order.po_number}</strong></span>}
          </div>
        )}

        <div className="bd-section">
          <label className="bd-section-title">Varelinjer</label>
          <table className="bd-lines-table">
            <thead>
              <tr>
                <th>Beskrivelse</th>
                <th>Antal</th>
                <th>Enhed</th>
                <th>EU</th>
                <th>½</th>
                <th>¼</th>
                <th>Temp.</th>
              </tr>
            </thead>
            <tbody>
              {lines ? lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.goods_description || '-'}</td>
                  <td>{l.quantity || '-'}</td>
                  <td>{l.unit || '-'}</td>
                  <td>{l.pallet_eu || '-'}</td>
                  <td>{l.pallet_half || '-'}</td>
                  <td>{l.pallet_quarter || '-'}</td>
                  <td>{l.temperature_requirement || '-'}</td>
                </tr>
              )) : (
                <tr>
                  <td>{order.goods_description || '-'}</td>
                  <td>{order.quantity || '-'}</td>
                  <td>{order.unit || '-'}</td>
                  <td>{order.pallet_eu || '-'}</td>
                  <td>{order.pallet_half || '-'}</td>
                  <td>{order.pallet_quarter || '-'}</td>
                  <td>{order.temperature_requirement || '-'}</td>
                </tr>
              )}
            </tbody>
          </table>
          {lines && lines.some(l => l.return_pallet_eu || l.return_pallet_half || l.return_pallet_quarter) && (
            <>
              <label className="bd-section-title bd-return-title">Retur emballage</label>
              <table className="bd-lines-table">
                <thead>
                  <tr><th>Beskrivelse</th><th>Retur EU</th><th>Retur ½</th><th>Retur ¼</th></tr>
                </thead>
                <tbody>
                  {lines.filter(l => l.return_pallet_eu || l.return_pallet_half || l.return_pallet_quarter).map((l, i) => (
                    <tr key={i}>
                      <td>{l.goods_description || '-'}</td>
                      <td>{l.return_pallet_eu || '-'}</td>
                      <td>{l.return_pallet_half || '-'}</td>
                      <td>{l.return_pallet_quarter || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {!isViewer && (
          <div className="bd-actions">
            {!order.locked && (
              <button className="bd-btn bd-btn-edit" onClick={() => { navigate(`/book?edit=${order.id}`); onClose(); }}>
                {order.approval_status === 'APPROVED' ? 'Anmod ændring' : 'Rediger'}
              </button>
            )}
            <button className="bd-btn bd-btn-copy" onClick={() => { navigate(`/book?copy=${order.id}`); onClose(); }}>
              Kopier
            </button>
            {order.locked && <span className="bd-locked-msg">Bookingen er låst og kan ikke ændres</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingDetail;
